import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import clsx from 'clsx';
import React, { FC, ReactNode } from 'react';

import CircularLoader, {
  CircularLoaderSize,
  CircularLoaderType,
} from '../circularLoader';

export interface ButtonProps {
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  darkBg?: boolean;
  customClassName?: string;
  fullWidth?: boolean;
  tabIndex?: number;
  type?: 'button' | 'submit' | 'reset' | undefined;
  text?: string;
  whitelabel?: boolean;
  isLoading?: boolean;
  id?: string;
  textColor?: string;
  disabledHover?: boolean;
}

const Button: FC<ButtonProps> = (props: ButtonProps) => {
  const {
    children,
    onClick,
    icon,
    disabled = false,
    darkBg = false,
    customClassName,
    fullWidth = false,
    tabIndex,
    type = 'button',
    text,
    whitelabel = false,
    isLoading = false,
    id,
    textColor,
    disabledHover = false,
    ...restProps
  } = props;

  const whiteLabel = useWhiteLabel();

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      tabIndex={tabIndex || 0}
      className={clsx(
        'align-middle aurego-button-color border-1px font-weight-bold h-[50px] inline-flex items-center justify-center rounded-default shadow-sm focus:ring-inset',
        'bg-red60 border-red50 text-grey10',
        !disabledHover && 'hover:bg-red70 hover:border-red70',
        'monochrome:bg-black monochrome:border-grey90 monochrome:hover:bg-grey90 monochrome:hover:border-grey90 monochrome:hover:text-white monochrome:text-white',
        'duration-100 ease-in-out transition-all',

        textColor && `text-${textColor}`,

        fullWidth && 'w-full',

        darkBg &&
          'disabled:bg-purple90 disabled:border-purple90 disabled:text-grey100 monochrome:bg-black monochrome:border-black monochrome:!text-white ',

        !darkBg &&
          'disabled:bg-grey30 disabled:border-grey30 monochrome:disabled:border-grey30 monochrome:disabled:bg-grey30 monochrome:disabled:hover:bg-grey20 monochrome:disabled:hover:border-grey20',
        {
          'sm:px-20px md:px-40px': icon === undefined,
          'px-[14px]': icon !== undefined,
        },

        whitelabel &&
          'wl-bg-highlight wl-bg-highlight--hover wl-custom-text-highlight  wl-border-highlight wl-border-highlight--hover wl-text-highlight wl-text-highlight--hover wl-no-green-border',

        isLoading &&
          disabled &&
          'disabled:bg-red60 disabled:border-red50 monochrome:disabled:border-grey30 monochrome:disabled:bg-grey30 monochrome:disabled:hover:bg-grey20 monochrome:disabled:hover:border-grey20',

        customClassName
      )}
      id={id}
      style={{
        color: String(whiteLabel?.brandButtonTextColor),
      }}
      {...restProps}
    >
      {isLoading ? (
        <CircularLoader
          type={
            isAuregoShop(whiteLabel)
              ? CircularLoaderType.secondary
              : CircularLoaderType.primary
          }
          customSize={CircularLoaderSize.sm}
        />
      ) : (
        <>
          {icon}
          {children}
          {text}
        </>
      )}
    </button>
  );
};

export default Button;
